import apiClient, { handleApiResponse, handleApiError } from './api';
import { Agency } from '../types';

export interface UpdateProfileData {
  businessName?: string;
  displayName?: string;
  email?: string;
  numberOfCars?: number;
  description?: string;
  ownerInfo?: {
    firstName?: string;
    lastName?: string;
    position?: string;
  };
  address?: {
    street?: string;
    city?: string;
    governorate?: string;
    country?: string;
    postal_code?: string;
  };
  location?: {
    type: 'Point';
    coordinates: [number, number]; // [longitude, latitude]
  };
  serviceOptions?: {
    homeDelivery?: boolean;
    airportPickup?: boolean;
    chauffeurService?: boolean;
    gpsTracking?: boolean;
    insuranceIncluded?: boolean;
  };
}

export interface ProfileResponse {
  success: boolean;
  message: string;
  data: {
    agency: Agency;
  };
}

export interface BasicInfoData {
  businessName: string;
  displayName: string;
  numberOfCars: number;
}

export interface AgencyStatusResponse {
  status: Agency['status'];
  statusUpdatedAt?: string;
  isOpenForBusiness?: boolean;
  phone_verified?: boolean;
  profileComplete?: boolean;
  updatedAt?: string;
}

let profileFetchPromise: Promise<Agency> | null = null;
let profileCache: Agency | null = null;
let profileLastFetchedAt = 0;
const PROFILE_MIN_REFETCH_MS = 10_000;

const unwrapAgencyProfile = (result: any): Agency => {
  if (result.data && result.data.agency) {
    return result.data.agency;
  }

  return result.data;
};

const fetchAgencyProfile = async (): Promise<Agency> => {
  const now = Date.now();

  if (profileFetchPromise) {
    return profileFetchPromise;
  }

  if (profileCache && now - profileLastFetchedAt < PROFILE_MIN_REFETCH_MS) {
    return profileCache;
  }

  profileFetchPromise = (async () => {
    try {
      const response = await apiClient.get('/agency/profile');
      const result = handleApiResponse(response);
      const agency = unwrapAgencyProfile(result);
      profileCache = agency;
      profileLastFetchedAt = Date.now();
      return agency;
    } catch (error) {
      throw new Error(handleApiError(error));
    } finally {
      profileFetchPromise = null;
    }
  })();

  return profileFetchPromise;
};

/**
 * Profile Service - Handles agency profile operations
 */
const profileService = {
  /**
   * Get current agency profile
   * @returns Promise<Agency>
   */
  getProfile: async (): Promise<Agency> => {
    return fetchAgencyProfile();
  },

  /**
   * Update agency profile
   * @param updates - Partial profile data to update
   * @returns Promise<Agency>
   */
  updateProfile: async (updates: UpdateProfileData): Promise<Agency> => {
    try {
      const response = await apiClient.put('/agency/profile', updates);
      const result = handleApiResponse(response);
      
      // Backend returns data.agency, not just data
      if (result.data && (result.data as any).agency) {
        const agency = (result.data as any).agency as Agency;
        profileCache = agency;
        profileLastFetchedAt = Date.now();
        return agency;
      }

      const agency = result.data as Agency;
      profileCache = agency;
      profileLastFetchedAt = Date.now();
      return agency;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get agency by slug (public profile)
   * @param slug - Agency slug
   * @returns Promise<Agency>
   */
  getAgencyBySlug: async (slug: string): Promise<Agency> => {
    try {
      const response = await apiClient.get<ProfileResponse>(`/agency/slug/${slug}`);
      const result = handleApiResponse(response);
      
      // Backend returns data at response.data.agency
      if (result.data && (result.data as any).agency) {
        return (result.data as any).agency as Agency;
      }
      
      // If data is already an Agency object (direct structure)
      return result.data as unknown as Agency;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Refresh profile status (polling for approval updates)
   * @returns Promise<AgencyStatusResponse>
   */
  refreshStatus: async (): Promise<AgencyStatusResponse> => {
    try {
      const response = await apiClient.get('/agency/status');
      const result = handleApiResponse(response);
      return result.data as AgencyStatusResponse;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Submit basic agency information (after phone verification)
   * @param basicInfo - Basic agency information
   * @returns Promise<Agency>
   */
  submitBasicInfo: async (basicInfo: BasicInfoData): Promise<Agency> => {
    try {
      const response = await apiClient.post('/agency/basic-info', basicInfo);
      const result = handleApiResponse(response);
      
      if (result.data && (result.data as any).agency) {
        const agency = (result.data as any).agency as Agency;
        profileCache = agency;
        profileLastFetchedAt = Date.now();
        return agency;
      }
      
      const agency = result.data as Agency;
      profileCache = agency;
      profileLastFetchedAt = Date.now();
      return agency;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Submit complete profile for admin review
   * @returns Promise<{ status: string; submittedAt: string }>
   */
  submitForReview: async (): Promise<{ status: string; submittedAt: string }> => {
    try {
      const response = await apiClient.post('/agency/submit-for-review');
      const result = handleApiResponse(response);
      
      return result.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }
};

export { profileService };
export default profileService;
